import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import messages from './messages';
import utilMessages from '../../utils/messages';
import {
  calculateSubtotal,
  calculateTotalDiscounts,
  calculateTotalGiftCards,
  calculateTotalPromo,
  calculatetShippingTotal,
  getTotalItems,
  priceToString,
  calculateTotalPromoBedding,
  calculateTotalPromoBeddingRemaining,
  findWidgetStatus,
  handleShowingTaxClick,
} from '../../utils';
import EstimatedTaxCalculator from '../EstimatedTaxCalculator';
import PromoCode from '../PromoCode';
import styles from './OrderTotalsSummary.module.scss';
import WhatIsThisFee from '../WhatIsThisFee';
import {
  DEFAULT_IN_HOME_SETUP_PRICE_CENT,
  DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
  IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
  IN_HOME_SETUP_QUALIFIED_SHIPPING_METHOD_KEY,
  WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  CHARGE_AFTER_WIDGET_TAGS,
  AFFIRM_WIDGET_TEST_VARIANTS,
  SHOW_AFFIRM_BLOCK_ENUM,
  AFFIRM_PRICE_LOWER_THRESHOLD,
  AFFIRM_PRICE_UPPER_THRESHOLD,
  AFFIRM_PRICE_CONDITIONAL_THRESHOLD,
  /* STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE, */
} from '../../utils/common/constants';
import { selectIsOverwritePaidInHomeShippingABTest } from '../../store/selectors/cart';
import { getCookie } from '../../utils/common';
import ChargeAfterWidget from '../ChargeAfterWidget';
import AffirmAsLowAs from '../AffirmAsLowAs';
import useDrupalSettings from '../../hooks/useDrupalSettings';
import MobileOrderSummary from '../MobileOrderSummary';

const OrderTotalsSummary = ({
  children,
  showPayAsLow,
  isPromoSectionDisabled,
  isSteppedCheckout,
  isDraftOrder,
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const lineItems = useSelector((state) => state.cart.lineItems);
  const [isMobileOrderSummaryOpen, setMobileOrderSummaryOpen] = useState(false);
  const [showEstimatedTaxCalculator, setShowEstimatedTaxCalculator] =
    useState(false);

  const affirmPromoABTest = window?.purTogglesEnabled('affirm_promo') ?? false;
  const { affirmHide, affirmShow, affirmUpdate } = SHOW_AFFIRM_BLOCK_ENUM;

  /*
    Replaced by isStripeAffirm:
    const isStripeRegularCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
  ); */

  const isStripeAffirm = window?.purTogglesEnabled?.('stripe_affirm');

  const giftCards = useSelector((state) => state.cart.giftCards);
  const customLineItems = useSelector((state) => state.cart.customLineItems);
  const taxes = useSelector((state) => {
    if (state.cart.taxedPrice) {
      return state.cart.taxedPrice.taxPortions.reduce(
        (totalTaxAmount, taxPortion) =>
          totalTaxAmount + taxPortion.amount.centAmount,
        0,
      );
    }

    return state.cart.estimatedTaxAmount?.centAmount || null;
  });
  const isOverwritePaidInHomeShippingABTest = useSelector(
    selectIsOverwritePaidInHomeShippingABTest,
  );
  const { selectedShippingMethods } = useSelector(
    (state) => state.shippingMethods,
  );
  const isCheckoutPage = window.location.pathname === '/checkout';
  const isOverwriteInHomeSetupShippingMethodSelected =
    isOverwritePaidInHomeShippingABTest &&
    (!isCheckoutPage ||
      (isCheckoutPage &&
        !!selectedShippingMethods?.find(
          (methods) =>
            methods.parentKey === IN_HOME_SETUP_QUALIFIED_SHIPPING_METHOD_KEY &&
            methods.groups?.[0]?.key === IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
        )));

  const selectedShippingMethodsCost = selectedShippingMethods?.reduce(
    (prev, curr) =>
      prev +
      (curr.groups?.reduce(
        (groupPrevValue, groupCurrValue) =>
          groupPrevValue + groupCurrValue.cost,
        0,
      ) ?? 0),
    0,
  );

  const warrantyItems = customLineItems.filter(
    (customLineItem) =>
      customLineItem.custom?.fields?.type === WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  );
  const cartHaveTaxesFromShippingAddress = useSelector(
    ({ cart }) => !!cart.taxedPrice,
  );
  const zipcode = useSelector(
    ({ cart }) => cart.estimatedTaxZipCode || cart.shippingAddress?.postalCode,
  );
  const recyclingFee = useSelector(
    (state) =>
      state.cart?.customLineItems?.find(
        (customLineItem) => customLineItem?.slug === 'custom_recyclingFee',
      )?.totalPrice.centAmount,
  );

  const deliveryFee = useSelector(
    (state) =>
      state.cart?.customLineItems?.find(
        (customLineItem) =>
          customLineItem.custom?.fields?.type ===
          DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
      )?.totalPrice.centAmount,
  );

  const mattressRemoval = customLineItems?.find(
    (customLineItem) => customLineItem?.custom?.fields?.mattress_removal,
  );

  const {
    taxedPrice,
    totalPrice,
    shippingAddress,
    estimatedTaxZipCode,
    estimatedTaxAmount,
  } = useSelector(({ cart }) => cart);

  const totalFromStore = useMemo(() => {
    if (
      taxedPrice &&
      (!estimatedTaxZipCode ||
        shippingAddress?.postalCode === estimatedTaxZipCode)
    ) {
      return taxedPrice?.totalGross;
    }

    const estimatedTax = estimatedTaxAmount?.centAmount || 0;

    return {
      ...totalPrice,
      centAmount: totalPrice.centAmount + estimatedTax,
    };
  }, [
    taxedPrice,
    shippingAddress?.postalCode,
    estimatedTaxZipCode,
    estimatedTaxAmount?.centAmount,
    totalPrice,
  ]);

  const total = useMemo(
    () => ({
      centAmount: Math.max(
        0,
        totalFromStore.centAmount -
          calculateTotalGiftCards({ giftCards }) -
          calculatetShippingTotal({ customLineItems }) +
          selectedShippingMethodsCost,
      ),
      currencyCode: totalFromStore.currencyCode,
      fractionDigits: totalFromStore.fractionDigits,
    }),
    [
      totalFromStore.centAmount,
      totalFromStore.currencyCode,
      totalFromStore.fractionDigits,
      giftCards,
      customLineItems,
      selectedShippingMethodsCost,
    ],
  );
  const showAffirm = useMemo(() => {
    if (!total || typeof total.centAmount !== 'number') {
      return affirmHide;
    }
    if (
      total.centAmount < AFFIRM_PRICE_LOWER_THRESHOLD ||
      total.centAmount > AFFIRM_PRICE_UPPER_THRESHOLD
    ) {
      return affirmHide;
    }
    if (
      total.centAmount >= AFFIRM_PRICE_CONDITIONAL_THRESHOLD &&
      total.centAmount < AFFIRM_PRICE_UPPER_THRESHOLD
    ) {
      return affirmUpdate;
    }
    return affirmShow;
  }, [affirmHide, affirmShow, affirmUpdate, total]);

  const { featureToggle } = useDrupalSettings();

  const { showChargeAfterWidget, showAffirmWidget } = findWidgetStatus({
    featureToggle,
    total,
  });

  const subtotal = useMemo(
    () => ({
      centAmount: calculateSubtotal({ lineItems }),
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [lineItems, total.currencyCode, total.fractionDigits],
  );

  const totalDiscount = useMemo(
    () => ({
      centAmount: calculateTotalDiscounts({ lineItems }),
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [lineItems, total.currencyCode, total.fractionDigits],
  );
  const totalPromoBeddingDiscount = useMemo(
    () => ({
      centAmount: calculateTotalPromoBedding({ lineItems }),
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [lineItems, total.currencyCode, total.fractionDigits],
  );
  const totalPromoBeddingDiscountRemaining = useMemo(
    () => ({
      centAmount: calculateTotalPromoBeddingRemaining({ lineItems }),
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [lineItems, total.currencyCode, total.fractionDigits],
  );

  const totalPromo = useMemo(
    () => ({
      centAmount: calculateTotalPromo({ lineItems }),
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [lineItems, total.currencyCode, total.fractionDigits],
  );

  const totalGiftCards = calculateTotalGiftCards({ giftCards });
  const totalGiftCardsApplied = useMemo(
    () => ({
      centAmount:
        totalGiftCards >= totalFromStore.centAmount
          ? totalFromStore.centAmount
          : totalGiftCards,
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [
      total.currencyCode,
      total.fractionDigits,
      totalFromStore.centAmount,
      totalGiftCards,
    ],
  );

  const totalShipping = useMemo(
    () => ({
      centAmount: selectedShippingMethodsCost,
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [total.currencyCode, total.fractionDigits, selectedShippingMethodsCost],
  );

  const totalWarranties = useMemo(
    () => ({
      centAmount: calculateSubtotal({ lineItems: warrantyItems }),
      currencyCode: total.currencyCode,
      fractionDigits: total.fractionDigits,
    }),
    [warrantyItems, total.currencyCode, total.fractionDigits],
  );

  const inHomeSetupPricing = priceToString({
    price: {
      centAmount:
        getCookie(process.env.CUSTOM_IN_HOME_SETUP_PRICING_AB_TEST_COOKIE_NAME)
          ?.value ?? DEFAULT_IN_HOME_SETUP_PRICE_CENT,
      fractionDigits: 2,
      currencyCode: 'USD',
    },
    maximumFractionDigits: 0,
    formatNumber,
    formatMessage,
  });

  const lineItemsCount = getTotalItems(lineItems);
  const handleFlyoutClick = () => setMobileOrderSummaryOpen(true);

  return (
    <div
      className={classnames(styles['pricing-summary'], {
        [styles['cart-summary']]: !isSteppedCheckout && !isDraftOrder,
        [styles['summary-border']]: lineItemsCount > 3,
      })}
    >
      <div className={styles['pricing-breakdown']}>
        <div className={styles['breakdown-row']}>
          <div className={styles['breakdown-row-item-count']}>
            <span>{formatMessage(messages.pricingSubtotalTitle)}</span>
            <div
              data-testid="price-breakdown-items-desktop"
              role="button"
              tabIndex="0"
              onClick={
                lineItemsCount > 3 && isSteppedCheckout
                  ? () => handleFlyoutClick()
                  : undefined
              }
              onKeyUp={
                lineItemsCount > 3 && isSteppedCheckout
                  ? () => handleFlyoutClick()
                  : undefined
              }
              className={classnames({
                [styles['breakdown-row-flyout-link']]:
                  lineItemsCount > 3 && isSteppedCheckout,
              })}
            >
              <span>
                (
                {formatMessage(messages.priceBreakdownItems, {
                  count: lineItemsCount,
                })}
                )
              </span>
            </div>
            <MobileOrderSummary
              isOpen={isMobileOrderSummaryOpen}
              onClickClose={() => setMobileOrderSummaryOpen(false)}
            />
          </div>
          <div data-testid="price-breakdown-price">
            {priceToString({ price: subtotal, formatNumber, formatMessage })}
          </div>
        </div>
        {warrantyItems?.length > 0 && (
          <div className={styles['breakdown-row']}>
            <div>{formatMessage(messages.priceBreakdownWarranties)}</div>
            <div data-testid="price-warranties">
              {priceToString({
                price: totalWarranties,
                formatNumber,
                formatMessage,
              })}
            </div>
          </div>
        )}
        {totalDiscount.centAmount !== 0 && (
          <div className={styles['breakdown-row']}>
            <div className={styles['breakdown-row-discounts']}>
              {formatMessage(messages.priceBreakdownDiscountsAppliedTitle)}
            </div>
            <div className={styles['breakdown-row-discounts']}>
              {`-${priceToString({
                price: totalDiscount,
                formatNumber,
                formatMessage,
                forceDigit: true,
              })}`}
            </div>
          </div>
        )}
        {totalPromoBeddingDiscount?.centAmount ? (
          <div className={styles['breakdown-row']}>
            <div className={styles['breakdown-row-discounts']}>
              {formatMessage(messages.beddingPromoTitle, {
                remaining:
                  totalPromoBeddingDiscountRemaining?.centAmount > 0
                    ? priceToString({
                        price: totalPromoBeddingDiscountRemaining,
                        formatNumber,
                        formatMessage,
                        forceDigit: true,
                      })
                    : 0,
              })}
            </div>
            <div className={styles['breakdown-row-discounts']}>
              {`-${priceToString({
                price: totalPromoBeddingDiscount,
                formatNumber,
                formatMessage,
                forceDigit: true,
              })}`}
            </div>
          </div>
        ) : null}
        {totalPromo?.centAmount > 0 ? (
          <div className={styles['breakdown-row']}>
            <div className={styles['breakdown-row-discounts']}>
              {formatMessage(messages.priceBreakdownPromoCodesTitle)}
            </div>
            <div className={styles['breakdown-row-discounts']}>
              {`-${priceToString({
                price: totalPromo,
                formatNumber,
                formatMessage,
                forceDigit: true,
              })}`}
            </div>
          </div>
        ) : null}
        {totalGiftCardsApplied.centAmount !== 0 && (
          <div className={styles['breakdown-row']}>
            <div className={styles['breakdown-row-discounts']}>
              {formatMessage(messages.priceBreakdownGifsCardsTitle, {
                count: giftCards?.length,
              })}
            </div>
            <div className={styles['breakdown-row-discounts']}>
              {`-${priceToString({
                price: totalGiftCardsApplied,
                formatNumber,
                formatMessage,
                forceDigit: true,
              })}`}
            </div>
          </div>
        )}
        <div className={styles['breakdown-row']}>
          <div>
            {isOverwriteInHomeSetupShippingMethodSelected
              ? formatMessage(messages.priceBreakdownFreeInHomeShipping)
              : formatMessage(messages.priceBreakdownShipping)}
          </div>
          <div>
            {isOverwriteInHomeSetupShippingMethodSelected && (
              <span
                className={styles['breakdown-row-shipping-discounted-amount']}
              >
                {inHomeSetupPricing}
              </span>
            )}
            <span
              className={classnames({
                [styles['breakdown-row-shipping-deducted-free-amount']]:
                  isOverwriteInHomeSetupShippingMethodSelected,
              })}
            >
              {priceToString({
                price: totalShipping,
                formatNumber,
                formatMessage,
              })}
            </span>
          </div>
        </div>
        {mattressRemoval && (
          <div className={styles['breakdown-row']}>
            <div>
              {formatMessage(messages.priceBreakdownMattresRemovalTitle)}
            </div>
            <div>{formatMessage(utilMessages.free)}</div>
          </div>
        )}
        {recyclingFee && (
          <div className={styles['breakdown-row']}>
            <div className={styles['breakdown-row-recycling']}>
              {formatMessage(messages.priceBreakdownRecyclingFeeTitle)}
              <WhatIsThisFee />
            </div>
            <div>
              {priceToString({
                price: {
                  centAmount: recyclingFee,
                  currencyCode: total.currencyCode,
                  fractionDigits: total.fractionDigits,
                },
                formatNumber,
                formatMessage,
              })}
            </div>
          </div>
        )}
        {deliveryFee && (
          <div className={styles['breakdown-row']}>
            <div className={styles['breakdown-row-recycling']}>
              {formatMessage(messages.priceBreakdownDeliveryFeeTitle)}
              <WhatIsThisFee />
            </div>
            <div>
              {priceToString({
                price: {
                  centAmount: deliveryFee,
                  currencyCode: total.currencyCode,
                  fractionDigits: total.fractionDigits,
                },
                formatNumber,
                formatMessage,
              })}
            </div>
          </div>
        )}
        <div className={styles['breakdown-row']}>
          {!cartHaveTaxesFromShippingAddress || showEstimatedTaxCalculator ? (
            <EstimatedTaxCalculator
              showEstimatedTaxCalculator={showEstimatedTaxCalculator}
              handleShowingTaxClick={(toggleZipCode) =>
                handleShowingTaxClick(
                  toggleZipCode,
                  showEstimatedTaxCalculator,
                  setShowEstimatedTaxCalculator,
                )
              }
            />
          ) : (
            <>
              <div className={styles['breakdown-row-taxes']}>
                <div>{formatMessage(messages.priceBreakdownTaxesTitle)}</div>
                <div
                  className={styles['breakdown-row-taxes-zipcode']}
                  data-private
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowEstimatedTaxCalculator(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowEstimatedTaxCalculator(true);
                    }
                  }}
                >
                  {zipcode}
                </div>
              </div>
              <div>
                {taxes !== null
                  ? priceToString({
                      price: {
                        centAmount: taxes,
                        currencyCode: total.currencyCode,
                        fractionDigits: total.fractionDigits,
                      },
                      formatNumber,
                      formatMessage,
                    })
                  : formatMessage(
                      messages.priceBreakdownCalculateOnCheckoutTaxesTitle,
                    )}
              </div>
            </>
          )}
        </div>
        {!isPromoSectionDisabled && <PromoCode />}
      </div>
      <div className={styles['pricing-and-financing']}>
        <div className={styles['pricing-total']}>
          <div className={styles.total}>
            {formatMessage(messages.pricingTotalTitle)}
          </div>
          <div
            className={styles['order-total']}
            data-testid="pricing-subtotal-amount"
          >
            <div className={styles.total}>
              {priceToString({
                price: total,
                formatNumber,
                formatMessage,
                forceDigit: true,
              })}
            </div>
          </div>
        </div>
        {showPayAsLow && (
          <div className={styles.financing}>
            <div
              className={classnames(styles.affirm, {
                [styles.hidden]: !showChargeAfterWidget,
              })}
            >
              <ChargeAfterWidget
                tag={CHARGE_AFTER_WIDGET_TAGS.productShort}
                total={
                  total?.centAmount
                    ? total.centAmount / 10 ** total.fractionDigits
                    : 0
                }
                id="charge-after-cart-summary-widget"
              />
            </div>
            {isStripeAffirm && (
              <AffirmAsLowAs
                total={total?.centAmount}
                showAffirm={showAffirm}
              />
            )}
            {!isStripeAffirm &&
              (affirmPromoABTest ? (
                <div
                  className={styles['pricing-subtotal-finance-affirm-promo']}
                >
                  <AffirmAsLowAs
                    total={total?.centAmount}
                    skus={AFFIRM_WIDGET_TEST_VARIANTS.test.variant}
                  />
                </div>
              ) : (
                <div
                  className={classnames(styles.affirm, {
                    [styles.hidden]: !showAffirmWidget,
                  })}
                >
                  <AffirmAsLowAs
                    total={total?.centAmount}
                    skus={AFFIRM_WIDGET_TEST_VARIANTS.control.variant}
                    showAffirm={showAffirm}
                  />
                </div>
              ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

OrderTotalsSummary.propTypes = {
  children: PropTypes.node,
  showPayAsLow: PropTypes.bool,
  isPromoSectionDisabled: PropTypes.bool,
  isSteppedCheckout: PropTypes.bool,
  isDraftOrder: PropTypes.bool,
};

OrderTotalsSummary.defaultProps = {
  children: null,
  showPayAsLow: false,
  isPromoSectionDisabled: false,
  isSteppedCheckout: false,
  isDraftOrder: false,
};

export default OrderTotalsSummary;
